import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InfoIcon from "@mui/icons-material/Info";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";

function GeneralInformation({ client }) {
  const { t } = useTranslation();
  const [infoCount, setInfoCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const prevValue = useRef(0);
  const animationRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    let count = 0;
    if (client?.vatId) count++;
    if (client?.serviceName) count++;
    if (client?.email && client.email.length > 0) {
      count += client.email.length;
    }
    setInfoCount(count);
  }, [client]);

  useEffect(() => {
    if (infoCount === 0) {
      setDisplayCount(0);
      hasAnimatedRef.current = false;
      return;
    }

    const shouldAnimate = !hasAnimatedRef.current || prevValue.current !== infoCount;

    if (!shouldAnimate) {
      setDisplayCount(infoCount);
      return;
    }

    setIsAnimating(true);
    hasAnimatedRef.current = true;

    const startTime = Date.now();
    const startValue = prevValue.current;
    const endValue = infoCount;
    const duration = 2000;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutQuart);

      setDisplayCount(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        prevValue.current = endValue;
        setTimeout(() => {
          setIsAnimating(false);
        }, 100);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [infoCount]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (fallbackErr) {
        console.error("Copy failed:", fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <>
      {copySuccess && <div className="copy-success-message show">Email copied to clipboard!</div>}
      <div
        className={`user_box_container enhanced-credential-box general-info-box ${infoCount === 0 ? "empty-state" : ""}`}
      >
        <div className="box-decoration-element"></div>
        <div className="box-icon-container">
          <InfoIcon className="box-main-icon" />
        </div>
        <div className="user_box_title">{t("user.information")}</div>
        <div className="user_box_subtitle">{t("user.general_client_information")}</div>

        {infoCount === 0 ? (
          <div className="empty-state-message">
            <div className="empty-state-icon">
              <InfoIcon />
            </div>
            <div className="empty-state-text">No information available</div>
            <div className="empty-state-subtext">
              Client information will appear here once configured
            </div>
          </div>
        ) : (
          <>
            <div
              className={`user_box_number_credentials_monitored ${isAnimating ? "animating" : ""}`}
            >
              {displayCount}
              <div className="number-pulse-effect"></div>
            </div>

            <div className="info-details-preview">
              {client?.vatId && (
                <div className="info-item">
                  <div className="info-item-content">
                    <span className="info-label">{t("user.vat_id")}:</span>
                    <span className="info-value">{client.vatId}</span>
                  </div>
                </div>
              )}

              {client?.serviceName && (
                <div className="info-item">
                  <div className="info-item-content">
                    <span className="info-label">{t("user.service")}:</span>
                    <span className="info-value">{client.serviceName}</span>
                  </div>
                </div>
              )}

              {client?.email && client.email.length > 0 && (
                <div className="info-item">
                  <div className="info-item-content">
                    <span className="info-label">{t("user.emails_linked")}:</span>
                    <div className="email-tags-container">
                      {client.email.map((email, index) => (
                        <div
                          key={`email-info-${client.id}-${index}`}
                          className="email-tag"
                          onClick={() => copyToClipboard(email)}
                          title={email}
                        >
                          <span className="email-tag-text">{email}</span>
                          <div className="email-tag-copy">
                            <ContentCopyIcon style={{ fontSize: 12 }} />
                          </div>
                          <div className="email-tooltip">Click to copy: {email}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

GeneralInformation.propTypes = {
  client: PropTypes.object.isRequired,
};

export default GeneralInformation;
